import { useAuthStore } from '../store/auth';
import { useAppStore } from '../store/app';
import { useNavigate } from 'react-router-dom';
import { User, Mail, Phone, Building, Shield, LogOut, Bell, BellOff } from 'lucide-react';
import { UserRole } from '../types';

const roleLabels = {
  [UserRole.SUPER_ADMIN]: 'Super Admin',
  [UserRole.ADMIN]: 'Administrador',
  [UserRole.OPERATOR]: 'Operador',
};

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const {
    notificationPermission,
    requestNotificationPermission,
  } = useAppStore();

  const handleLogout = async () => {
    if (window.confirm('Deseja realmente sair?')) {
      await logout();
      navigate('/login', { replace: true });
    }
  };

  const handleNotificationToggle = async () => {
    if (notificationPermission === 'granted') {
      // Can't revoke permission programmatically
      alert('Para desativar notificações, vá nas configurações do navegador');
    } else if (notificationPermission === 'denied') {
      alert('Notificações foram bloqueadas. Ative nas configurações do navegador');
    } else {
      await requestNotificationPermission();
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 safe-top">
        <div className="px-4 py-4">
          <h1 className="text-2xl font-bold text-gray-900">Perfil</h1>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* User Info Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center">
              <User className="w-8 h-8 text-primary-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-xl font-semibold text-gray-900 truncate">
                {user.name}
              </h2>
              <p className="text-sm text-gray-500">
                {roleLabels[user.role as UserRole] || user.role}
              </p>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center text-gray-700">
              <Mail className="w-5 h-5 mr-3 text-gray-400 flex-shrink-0" />
              <span className="truncate">{user.email}</span>
            </div>

            {user.phone && (
              <div className="flex items-center text-gray-700">
                <Phone className="w-5 h-5 mr-3 text-gray-400 flex-shrink-0" />
                <span>{user.phone}</span>
              </div>
            )}

            <div className="flex items-center text-gray-700">
              <Building className="w-5 h-5 mr-3 text-gray-400 flex-shrink-0" />
              <span className="truncate">Organização: {user.organizationId}</span>
            </div>

            <div className="flex items-center text-gray-700">
              <Shield className="w-5 h-5 mr-3 text-gray-400 flex-shrink-0" />
              <span>Permissão: {roleLabels[user.role as UserRole]}</span>
            </div>
          </div>
        </div>

        {/* Settings */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Configurações</h3>
          </div>

          {/* Notifications */}
          <button
            onClick={handleNotificationToggle}
            className="w-full px-4 py-4 flex items-center justify-between tap-target hover:bg-gray-50 active:bg-gray-100 transition-colors"
          >
            <div className="flex items-center">
              {notificationPermission === 'granted' ? (
                <Bell className="w-5 h-5 mr-3 text-gray-400" />
              ) : (
                <BellOff className="w-5 h-5 mr-3 text-gray-400" />
              )}
              <div className="text-left">
                <p className="text-gray-900 font-medium">Notificações</p>
                <p className="text-sm text-gray-500">
                  {notificationPermission === 'granted'
                    ? 'Ativadas'
                    : notificationPermission === 'denied'
                    ? 'Bloqueadas'
                    : 'Desativadas'}
                </p>
              </div>
            </div>
            <div
              className={`w-11 h-6 rounded-full transition-colors ${
                notificationPermission === 'granted' ? 'bg-primary-600' : 'bg-gray-300'
              }`}
            >
              <div
                className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${
                  notificationPermission === 'granted' ? 'translate-x-6' : 'translate-x-0.5'
                } mt-0.5`}
              />
            </div>
          </button>
        </div>

        {/* App Info */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Sobre</h3>
          <div className="space-y-2 text-sm text-gray-600">
            <p>MindFlow Agenda</p>
            <p>Versão 1.0.0</p>
            <p className="text-xs text-gray-500 mt-3">
              © {new Date().getFullYear()} MindFlow. Todos os direitos reservados.
            </p>
          </div>
        </div>

        {/* Logout Button */}
        <button
          onClick={handleLogout}
          className="w-full bg-red-600 text-white py-3 rounded-lg font-semibold hover:bg-red-700 active:bg-red-800 tap-target flex items-center justify-center gap-2"
        >
          <LogOut className="w-5 h-5" />
          Sair
        </button>
      </div>
    </div>
  );
}
